/**
 * What the record page opens first (13.2-11; D-13.2-A-5, D-13.2-P-11), pure. Every panel and every read section on
 * the record page collapses; `openByDefault` says which start open, so the page opens on this person's next step and
 * everything else is one tap away.
 *
 * - **Panels:** the person's next step is the FIRST panel they can act on (not read only) whose stage the record has
 *   not moved past. Every read-only panel is closed. A panel named by the URL hash (`#panel-<key>`) is also open, so
 *   the deep links from Verify, Today and the chips land open.
 * - **Moved past** (needed so Oversight on an Evaluating incident opens Closure, not Triage, which it may still re-save):
 *   - the record's `Status` sets a floor: `New` → the report onward; `Actioning` → triage onward (a completed report
 *     is behind it); `Evaluating`, `Completed`, `Cancelled` → closure;
 *   - triage is the engine's one `Status` move (`TRIAGE_STATUS`): once the record has left `New`, a triage panel is a
 *     re-save, not the next step. The closure verbs need no rule here: their slot already offers only the moves the
 *     `Status` allows (`closureVerbsFor`).
 *   A slot's stage is its verbs' (`STAGE_OF_VERB`), so a slot added later (`complete`, stage lodge) needs no change
 *   here. 13.2-09: a slot may also say whether it holds waiting work (`nextStepWhen`); the complete slot does only
 *   while the report lacks a field triage can't add, so on a complete New report the next step stays triage. A slot whose writes aren't register verbs (`verbs: []`, e.g. Follow-ups) has no stage: it is never the
 *   default next step, and opens from its hash or a tap.
 * - **Sections:** *The report* is open, and so is the section that holds the next step's stage when the record has
 *   one. With no next step, *The report* is open, and *Closure* too when the record is Completed.
 * - **Areas:** the timeline and the closure links start closed, except on a closed record (Completed or Cancelled).
 *
 * The page keeps a person's toggles for the visit only (no storage), re-initialising from this when the ref changes.
 *
 * D-13.2-A-9 (Andy, 26 Oct 2026): the record page now opens with EVERYTHING collapsed (`start: 'collapsed'`), so the
 * person expands what they want; only a `#panel-<key>` deep link opens its panel. The rule above is `'nextStep'`, and
 * `nextStepPanel` still names the next step, which the page tags "Your next step".
 */

import { STAGE_OF_VERB } from '../config/fieldset/registerFields';
import { liveChoice, RECORD_REGISTERS } from '../config/fieldset/live';
import type { PanelKey, PanelSlot, PanelView } from '../components/record/panels/registry';
import type { RecordRow } from '../types/records';
import { SECTION_OF_STAGE, SECTION_ORDER } from './recordView';
import type { FieldSection, SectionId } from './recordView';
import { TRIAGE_STATUS } from './recordVerbs';

/** The record page's two collapsible areas beyond the panels and sections. */
export type RecordArea = 'trail' | 'links';

export interface OpenByDefault {
  panels: Set<PanelKey>;
  sections: Set<SectionId>;
  areas: Set<RecordArea>;
}

const [NEW, ACTIONING, EVALUATING, COMPLETED, CANCELLED] = liveChoice('incidents', 'Status', 'New', 'Actioning', 'Evaluating', 'Completed', 'Cancelled');
/** `Status` is the same five values on every register (each checked at load). */
for (const register of RECORD_REGISTERS) liveChoice(register, 'Status', NEW, ACTIONING, EVALUATING, COMPLETED, CANCELLED);

/** The earliest section a record at this `Status` still has a next step in. */
const FLOOR_OF_STATUS: Readonly<Record<string, SectionId>> = {
  [NEW]: 'report',
  [ACTIONING]: 'triage',
  [EVALUATING]: 'closure',
  [COMPLETED]: 'closure',
  [CANCELLED]: 'closure',
};

const rank = (id: SectionId): number => SECTION_ORDER.indexOf(id);

function statusOf(row: RecordRow): string {
  return typeof row.fields.Status === 'string' ? row.fields.Status : '';
}

/** Whether the record is closed (Completed or Cancelled). */
export function isClosedStatus(row: RecordRow): boolean {
  const status = statusOf(row);
  return status === COMPLETED || status === CANCELLED;
}

/** The read section a slot's stage sits in: its earliest verb's; `null` for a slot with no register verb. */
export function sectionOfSlot(slot: Pick<PanelSlot, 'verbs'>): SectionId | null {
  let best: SectionId | null = null;
  for (const verb of slot.verbs) {
    const stage = STAGE_OF_VERB[verb];
    if (!stage) continue;
    const section = SECTION_OF_STAGE[stage];
    if (best === null || rank(section) < rank(best)) best = section;
  }
  return best;
}

/** Is this panel the kind that can be the person's next step on this record? */
function isNextStep(view: PanelView, row: RecordRow): boolean {
  if (view.readOnly) return false;
  // 13.2-09: a slot that says whether it holds waiting work (Complete the report: only while a report field is missing).
  if (view.slot.nextStepWhen && !view.slot.nextStepWhen(row)) return false;
  const section = sectionOfSlot(view.slot);
  if (section === null) return false;
  const floor = FLOOR_OF_STATUS[statusOf(row)] ?? 'report';
  if (rank(section) < rank(floor)) return false;
  // Triage's Status move is made: once the record has left `New`, a triage-only panel is a re-save.
  const triageFrom = TRIAGE_STATUS[row.register]?.from;
  if (view.slot.verbs.every((v) => v === 'triage') && statusOf(row) !== triageFrom) return false;
  return true;
}

/** What a closed read section holds: "9 fields", or "9 fields, 4 not recorded". */
export function sectionHint(section: Pick<FieldSection, 'items'>): string {
  const n = section.items.length;
  const blank = section.items.filter((i) => i.value === null).length;
  const fields = `${n} ${n === 1 ? 'field' : 'fields'}`;
  return blank > 0 ? `${fields}, ${blank} not recorded` : fields;
}

/** The panel key a `#panel-<key>` hash names, or `null`. */
export function panelOfHash(hash: string | null | undefined): string | null {
  const m = /^#?panel-([A-Za-z][\w-]*)$/.exec((hash ?? '').trim());
  return m ? m[1] : null;
}

/** The person's next-step panel on this record, or `null`. */
export function nextStepPanel(views: readonly PanelView[], row: RecordRow): PanelView | null {
  return views.find((v) => isNextStep(v, row)) ?? null;
}

/**
 * Which panels, read sections and areas start open (see the header). `hash` is the URL hash (`#panel-closure`); a
 * panel it names is opened as well, when this person sees that panel. `start` is `'collapsed'` on the record page
 * (D-13.2-A-9: everything shut, the person expands what they want; the next step is tagged instead); `'nextStep'` is
 * 13.2-11's rule, kept for its tests and as the one-word way back.
 */
export function openByDefault(
  views: readonly PanelView[],
  sections: readonly Pick<FieldSection, 'id'>[],
  row: RecordRow,
  hash: string | null = null,
  start: 'collapsed' | 'nextStep' = 'collapsed',
): OpenByDefault {
  const panels = new Set<PanelKey>();
  const open = new Set<SectionId>();
  const present = new Set(sections.map((s) => s.id));
  const add = (id: SectionId) => {
    if (present.has(id)) open.add(id);
  };

  // D-13.2-A-9 (Andy, 26 Oct): the page opens with everything collapsed; only a deep link opens its panel.
  const wanted = panelOfHash(hash);
  if (wanted) {
    const hit = views.find((v) => v.slot.key === wanted);
    if (hit) panels.add(hit.slot.key);
  }
  if (start === 'nextStep') {
    const next = nextStepPanel(views, row);
    if (next) panels.add(next.slot.key);
    add('report');
    if (next) {
      const section = sectionOfSlot(next.slot);
      if (section) add(section);
    } else if (statusOf(row) === COMPLETED) {
      add('closure');
    }
  }

  const areas = new Set<RecordArea>(start === 'nextStep' && isClosedStatus(row) ? ['trail', 'links'] : []);
  return { panels, sections: open, areas };
}
